import React, { useState, useEffect } from 'react';

const HEADING_FONTS = [
  { value: 'Familjen Grotesk, sans-serif',   label: 'Familjen Grotesk',  category: 'Modern' },
  { value: 'Fraunces, serif',                 label: 'Fraunces',          category: 'Elegant' },
  { value: 'Montserrat, sans-serif',          label: 'Montserrat',        category: 'Modern' },
  { value: 'Poppins, sans-serif',             label: 'Poppins',           category: 'Modern' },
  { value: 'Lato, sans-serif',                label: 'Lato',              category: 'Clean' },
  { value: 'Roboto, sans-serif',              label: 'Roboto',            category: 'Clean' },
  { value: 'Open Sans, sans-serif',           label: 'Open Sans',         category: 'Clean' },
  { value: 'Playfair Display, serif',         label: 'Playfair Display',  category: 'Elegant' },
  { value: 'Georgia, serif',                  label: 'Georgia',           category: 'Classic' },
  { value: 'Arial, sans-serif',               label: 'Arial',             category: 'System' }
];

const BODY_FONTS = [
  { value: 'Wix Madefor Text, sans-serif',    label: 'Wix Madefor Text',  category: 'Readable' },
  { value: 'Inter, sans-serif',               label: 'Inter',             category: 'Readable' },
  { value: 'Open Sans, sans-serif',           label: 'Open Sans',         category: 'Readable' },
  { value: 'Roboto, sans-serif',              label: 'Roboto',            category: 'Readable' },
  { value: 'Lato, sans-serif',                label: 'Lato',              category: 'Readable' },
  { value: 'Source Sans Pro, sans-serif',     label: 'Source Sans Pro',   category: 'Readable' },
  { value: 'Nunito, sans-serif',              label: 'Nunito',            category: 'Friendly' },
  { value: 'Raleway, sans-serif',             label: 'Raleway',           category: 'Modern' },
  { value: 'Georgia, serif',                  label: 'Georgia',           category: 'Classic' },
  { value: 'Arial, sans-serif',               label: 'Arial',             category: 'System' }
];

const FONT_SIZES = [
  { value: 'small',  label: 'Small',  base: '13px', heading: '20px' },
  { value: 'medium', label: 'Medium', base: '15px', heading: '24px' },
  { value: 'large',  label: 'Large',  base: '17px', heading: '28px' }
];

function TypographyEditor({ typography, onUpdate, orgId, onSave, saving, savedMessage }) {
  const [headingFont, setHeadingFont] = useState(typography?.headingFont || HEADING_FONTS[0].value);
  const [bodyFont,    setBodyFont]    = useState(typography?.bodyFont    || BODY_FONTS[0].value);
  const [fontSize,    setFontSize]    = useState(typography?.fontSize    || 'medium');

  useEffect(() => {
    // Load Google Fonts
    const googleFonts = [
      'Familjen+Grotesk:wght@400;500;600;700',
      'Fraunces:wght@400;600;800',
      'Montserrat:wght@400;600;700',
      'Poppins:wght@400;500;600',
      'Wix+Madefor+Text:wght@400;500;600',
      'Open+Sans:wght@400;600',
      'Lato:wght@400;700',
      'Roboto:wght@400;500;700',
      'Inter:wght@400;500;600',
      'Playfair+Display:wght@400;600',
      'Nunito:wght@400;600',
      'Raleway:wght@400;600'
    ].join('&family=');
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${googleFonts}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    if (typography) {
      setHeadingFont(typography.headingFont || HEADING_FONTS[0].value);
      setBodyFont(typography.bodyFont || BODY_FONTS[0].value);
      setFontSize(typography.fontSize || 'medium');
    }
  }, [typography]);

  const update = (patch) => {
    const updated = { headingFont, bodyFont, fontSize, ...patch };
    if (patch.headingFont !== undefined) setHeadingFont(patch.headingFont);
    if (patch.bodyFont !== undefined)    setBodyFont(patch.bodyFont);
    if (patch.fontSize !== undefined)    setFontSize(patch.fontSize);
    onUpdate(updated);
  };

  const sizeConfig = FONT_SIZES.find(s => s.value === fontSize) || FONT_SIZES[1];

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>Typography</h2>
          <p className="bb-editor-desc">Choose fonts and sizes for your community portal.</p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Heading Font</h3>
        <div className="bb-form-group">
          <label htmlFor="headingFont">Font Family</label>
          <select id="headingFont" className="bb-select" value={headingFont}
            onChange={(e) => update({ headingFont: e.target.value })}>
            {HEADING_FONTS.map(f => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label} — {f.category}
              </option>
            ))}
          </select>
          <div className="bb-font-preview" style={{ fontFamily: headingFont, fontSize: sizeConfig.heading }}>
            Building Stronger Communities Together
          </div>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Body Font</h3>
        <div className="bb-form-group">
          <label htmlFor="bodyFont">Font Family</label>
          <select id="bodyFont" className="bb-select" value={bodyFont}
            onChange={(e) => update({ bodyFont: e.target.value })}>
            {BODY_FONTS.map(f => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label} — {f.category}
              </option>
            ))}
          </select>
          <div className="bb-font-preview bb-font-preview--body" style={{ fontFamily: bodyFont, fontSize: sizeConfig.base }}>
            Welcome to our community! We're dedicated to supporting every member on their journey.
            Our events, programmes, and peer connections help you thrive.
          </div>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Font Size</h3>
        <div className="bb-size-options">
          {FONT_SIZES.map(s => (
            <button key={s.value} type="button"
              className={`bb-size-option ${fontSize === s.value ? 'active' : ''}`}
              onClick={() => update({ fontSize: s.value })}>
              <span className="bb-size-label">{s.label}</span>
              <span className="bb-size-hint">{s.base} base</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Combined Preview</h3>
        <div className="bb-preview-card">
          <h3 style={{ fontFamily: headingFont, fontSize: sizeConfig.heading, margin: '0 0 12px', color: 'var(--bb-primary)' }}>
            Welcome to Our Community
          </h3>
          <p style={{ fontFamily: bodyFont, fontSize: sizeConfig.base, lineHeight: 1.7, color: '#444', margin: '0 0 16px' }}>
            We are here to support, connect, and empower every member. Browse our upcoming events,
            join a support group, or connect with a peer mentor today.
          </p>
          <button style={{ fontFamily: bodyFont, fontSize: sizeConfig.base, backgroundColor: 'var(--bb-primary)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
            View Events
          </button>
        </div>
      </div>
    </div>
  );
}

export default TypographyEditor;
